import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { getFileDownloadUrl } from "../../../../../../../api/firebase/firestoreService";
import { ToastMessages } from "../../../../../../../components/toasts/toastMessages";

/**
 * Downloads the Provider List Excel file for a specific healthcare facility from Firebase Storage.
 *
 * The function fetches the file from the path `payer-utils/{facilityId}/Provider_List.xlsx`,
 * creates a blob URL, and triggers a browser download with the filename "Provider_List.xlsx".
 *
 * @async
 * @param {string} selectedHealthCareFacility - The ID of the healthcare facility to download the provider list for
 * @param {(isDownloading: boolean) => void} setIsDownloading - Callback to update the download loading state
 * @returns {Promise<void>}
 *
 * @throws Will show an error toast if the download URL cannot be retrieved or if the download fails
 *
 * @example
 * const [isDownloading, setIsDownloading] = useState(false);
 *
 * const onDownloadClick = () => {
 *   handleDownload("facility-123", setIsDownloading);
 * };
 */
export const handleDownload = async (
  selectedHealthCareFacility: string,
  setIsDownloading: (isDownloading: boolean) => void,
) => {
  try {
    setIsDownloading(true);

    const filePath = `payer-utils/${selectedHealthCareFacility}/Provider_List.xlsx`;
    const downloadUrl = await getFileDownloadUrl(filePath);
    if (!downloadUrl) {
      throw new Error("Failed to get download URL");
    }

    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error("Download failed");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "Provider_List.xlsx";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading file:", error);
    toast.error(ToastMessages.PROVIDER_LIST_DOWNLOAD_ERROR);
  } finally {
    setIsDownloading(false);
  }
};
